import { useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { UfoCustomTimingWrapper } from "@/components/signup/utils/ufo-custom-timing-wrapper";
import { ufoEventRedirectToProduct } from "@/components/signup/utils/ufo-events";
import { UfoCustomTimingKey } from "@/components/signup/utils/ufo.types";

import { useAnalyticsClient } from "../../../../../../../../analytics/wac-analytics/analytics-client";
import { DwellTimeWrapper } from "../../components/dwell-time-wrapper/dwell-time-wrapper";
import { MessageContainerProps } from "../../components/styles";
import { useIsJsmCrossflowConfirmation } from "../../hooks/use-is-jsm-crossflow-confirmation";
import ProgressBar from "../../sections/progress-bar";
import { ServCoOnboardingContainer } from "../serv-co/serv-co-onboarding-container/serv-co-onboarding-container";

import { ServiceCollectionProvisioningAnimation } from "./components/service-collection-provisioning-animation";

const messageContainerCss = css(MessageContainerProps);

const innerStackCss = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px"
});

export const ServiceCollectionProvisioningScreenLofi = () => {
    const intl = useIntl();
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const isJsmCrossflow = useIsJsmCrossflowConfirmation();

    useEffect(() => {
        if (analyticsClient) {
            createAnalyticsEvent({
                eventName: "serviceCollectionProvisioningPageViewed",
                eventComponent: "serviceCollectionProvisioningPage",
                event: "viewed",
                attributes: { variant: "servCoLofiBackground" }
            }).fire("signup");
        }
    }, [analyticsClient, createAnalyticsEvent]);

    const header = intl.formatMessage({
        id: "WacConfirmationPage.serviceCollectionProvisioning.lofi.header",
        defaultMessage: "Creating your workspace..."
    });

    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={
                UfoCustomTimingKey.SERVICE_COLLECTION_PROVISIONING_VIEW_DWELL_TIME
            }
        >
            <DwellTimeWrapper
                eventData={{
                    eventName: "serviceCollectionProvisioningPageDwelled",
                    eventComponent: "serviceCollectionProvisioningPage",
                    event: "dwelled",
                    actionSubject: "serviceCollectionProvisioningPage",
                    source: "bxp-web-platform-wac-confirmation-page"
                }}
                initialPollInterval={2000}
                idleTimeout={120000}
                nextPollIntervalCallback={(x: number) => x + 2000}
            >
                <ServCoOnboardingContainer
                    header={header}
                    subheader=""
                    hideSubHeader
                    size="lg"
                    hideLogo
                    isJsmCrossflow={isJsmCrossflow}
                >
                    <div css={innerStackCss}>
                        <ServiceCollectionProvisioningAnimation />
                        <ProgressBar />
                        <p
                            css={messageContainerCss}
                            data-testid="service-collection-provisioning-message-lofi"
                        >
                            <FormattedMessage
                                id="WacConfirmationPage.serviceCollectionProvisioning.lofi.body"
                                defaultMessage="Let's start by creating your first space."
                            />
                        </p>
                    </div>
                </ServCoOnboardingContainer>
            </DwellTimeWrapper>
        </UfoCustomTimingWrapper>
    );
};

export default ServiceCollectionProvisioningScreenLofi;
